import type { AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';
import type { Auth } from 'firebase/auth';

import type { WebEnvT } from '../config/env';

export type LotusAuthOptionsT = Readonly<{
  requireAuth?: boolean;
}>;

export type LotusRequestOptionsT = LotusAuthOptionsT & Readonly<{
  signal?: AbortSignal;
}>;

export async function getAccessToken(auth: Auth): Promise<string | null> {
  const user = auth.currentUser;
  if (!user) {
    return null;
  }
  return user.getIdToken();
}

export async function createLotusAxiosConfig(
  auth: Auth,
  env: Pick<WebEnvT, 'apiBaseUrl'>,
  options: LotusAuthOptionsT = {}
): Promise<AxiosRequestConfig> {
  const token = await getAccessToken(auth);
  if (!token) {
    if (options.requireAuth === false) {
      return {
        baseURL: env.apiBaseUrl,
      };
    }

    throw new Error('Cannot create Lotus API config: user is not authenticated');
  }

  return {
    baseURL: env.apiBaseUrl,
    headers: {
      Authorization: `Bearer firebase:${token}`,
    },
  };
}

export async function createLotusRequestConfig(
  auth: Auth,
  env: Pick<WebEnvT, 'apiBaseUrl'>,
  options: LotusRequestOptionsT = {}
): Promise<AxiosRequestConfig> {
  const config = await createLotusAxiosConfig(auth, env, options);
  return {
    ...config,
    signal: options.signal,
  };
}

export function createLotusRequestInterceptor(
  auth: Auth,
  env: Pick<WebEnvT, 'apiBaseUrl'>,
  options: LotusAuthOptionsT = {}
): (config: InternalAxiosRequestConfig) => Promise<InternalAxiosRequestConfig> {
  return async (config) => {
    const lotusConfig = await createLotusAxiosConfig(auth, env, options);
    const authHeader = (lotusConfig.headers as { Authorization?: string } | undefined)?.Authorization;

    config.baseURL ??= lotusConfig.baseURL;
    if (authHeader) {
      config.headers.Authorization = authHeader;
    }

    return config;
  };
}
